import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react'

import { Icon } from '@/shared/components'
import { pxToRem } from '@/shared/styles/themes'
import { faPlus, faTimes } from '@fortawesome/pro-regular-svg-icons'
import { Box, IconButton, Tab, Tabs, Tooltip, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'

export type TabItem = {
  id: string | number
  label: string
  content: ReactNode
  color?: string
  icon?: ReactNode

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any
}

export type TabbedWindowProps = {
  tabs: TabItem[]
  activeTabIndex: number | null
  onTabChange: (newIndex: number) => void
  onTabClose?: (index: number) => void
  onAddTab?: () => void
  showAddButton?: boolean
  className?: string
  disableTabRipple?: boolean
  maxLabelWidth?: number
  tabPanelClassName?: string
  onTabsReorder?: (newTabs: TabItem[]) => void
  emptyStateContent?: ReactNode
}

/**
 * A reusable component for managing tabbed windows.
 * Supports tab navigation, closing tabs, adding new tabs, and drag-and-drop reordering.
 */
export const TabbedWindow: React.FC<TabbedWindowProps> = ({
  tabs,
  activeTabIndex,
  onTabChange,
  onTabClose,
  onAddTab,
  showAddButton = false,
  className,
  disableTabRipple = true,
  maxLabelWidth = 80,
  tabPanelClassName,
  onTabsReorder,
  emptyStateContent,
}) => {
  const [draggedTab, setDraggedTab] = useState<number | null>(null)
  const [dragOverTab, setDragOverTab] = useState<number | null>(null)
  const [dropIndicator, setDropIndicator] = useState<{ show: boolean; left: number; top: number; height: number }>({
    show: false,
    left: 0,
    top: 0,
    height: 0,
  })
  const [isDragging, setIsDragging] = useState(false)
  const tabsRef = useRef<HTMLDivElement>(null)
  const dragTimeout = useRef<number | null>(null)

  // Handle tab change
  const handleTabChange = useCallback((_: React.SyntheticEvent, newValue: number) => {
    // Ignore tab change during drag operations
    if (isDragging) return

    // Check if special "add" tab was clicked
    if (showAddButton && ((tabs.length > 0 && newValue === tabs.length) || (tabs.length === 0 && newValue === 0))) {
      if (onAddTab) onAddTab()

      return
    }

    // Only change to an existing tab
    if (newValue < tabs.length) {
      onTabChange(newValue)
    }
  }, [tabs.length, onTabChange, onAddTab, showAddButton, isDragging])

  // Handle close button click
  const handleTabClose = useCallback((index: number, event: React.MouseEvent) => {
    event.stopPropagation() // Prevent tab selection
    if (onTabClose) onTabClose(index)
  }, [onTabClose])

  // Drag and drop handlers for tab reordering
  const handleDragStart = (event: React.DragEvent, index: number) => {
    if (!onTabsReorder) return

    // Stop propagation but don't prevent default - we need the browser's drag behavior
    event.stopPropagation()

    // Set the drag data
    event.dataTransfer.setData('text/plain', index.toString())
    event.dataTransfer.effectAllowed = 'move'

    // Create a ghost element for dragging
    if (event.currentTarget instanceof HTMLElement) {
      const rect = event.currentTarget.getBoundingClientRect()
      const offsetX = event.clientX - rect.left
      const offsetY = event.clientY - rect.top

      const ghostElement = event.currentTarget.cloneNode(true) as HTMLElement

      ghostElement.style.width = `${rect.width}px`
      ghostElement.style.position = 'absolute'
      ghostElement.style.top = '-1000px'
      ghostElement.style.opacity = '0.8'
      document.body.appendChild(ghostElement)

      event.dataTransfer.setDragImage(ghostElement, offsetX, offsetY)

      // Remove ghost element after drag starts
      requestAnimationFrame(() => {
        if (document.body.contains(ghostElement)) {
          document.body.removeChild(ghostElement)
        }
      })

      // Set opacity of dragged element
      event.currentTarget.style.opacity = '0.4'
    }

    requestAnimationFrame(() => {
      setIsDragging(true)
      setDraggedTab(index)
    })
  }

  const updateDropIndicator = (event: React.DragEvent, index: number) => {
    if (!tabsRef.current || draggedTab === null || !onTabsReorder) return

    try {
      const tabElements = Array.from(tabsRef.current.querySelectorAll('.MuiTab-root'))

      if (tabElements.length === 0) return

      const tabElement = tabElements[index] as HTMLElement

      if (!tabElement) return

      const tabsContainerRect = tabsRef.current.getBoundingClientRect()

      // Get the tab element's rect
      const tabRect = tabElement.getBoundingClientRect()
      const mouseX = event.clientX

      const isBeforeTab = mouseX < tabRect.left + tabRect.width / 2
      const indicatorLeft = isBeforeTab ?
        tabRect.left - tabsContainerRect.left :
        tabRect.right - tabsContainerRect.left

      // Update state only if values have changed significantly to reduce rerenders
      setDropIndicator(prev => {
        if (Math.abs(prev.left - indicatorLeft) > 2 || prev.show !== true || prev.height !== tabRect.height) {
          return {
            show: true,
            left: indicatorLeft,
            top: 0, // Position at the top of container
            height: tabRect.height,
          }
        }

        return prev
      })
    } catch (error) {
      // In case of any errors, hide the indicator
      setDropIndicator(prev => ({ ...prev, show: false }))
      console.error('Error updating drop indicator:', error)
    }
  }

  const handleDragOver = (event: React.DragEvent, index: number) => {
    // This is critical - must prevent default to allow dropping
    event.preventDefault()

    if (draggedTab === null || !onTabsReorder || draggedTab === index) return

    // Set the drop effect
    event.dataTransfer.dropEffect = 'move'

    // Update the drop indicator
    updateDropIndicator(event, index)

    // Throttle state updates to prevent performance issues
    if (dragTimeout.current !== null) {
      window.clearTimeout(dragTimeout.current)
    }

    dragTimeout.current = window.setTimeout(() => {
      setDragOverTab(index)
    }, 50)
  }

  const handleDragEnter = (event: React.DragEvent, index: number) => {
    event.preventDefault()
    if (draggedTab === null || !onTabsReorder) return

    // Update the drop indicator
    updateDropIndicator(event, index)
  }

  const handleDrop = (event: React.DragEvent, index: number) => {
    // Prevent default browser behavior
    event.preventDefault()
    event.stopPropagation()

    if (draggedTab === null || !onTabsReorder || draggedTab === index) {
      resetDragState()

      return
    }

    // Perform the reordering
    const newTabs = [...tabs]
    const draggedTabItem = newTabs[draggedTab]

    newTabs.splice(draggedTab, 1)
    newTabs.splice(index, 0, draggedTabItem)

    // Calculate new active tab index
    let newActiveTabIndex = activeTabIndex

    if (activeTabIndex === draggedTab) {
      // If dragging the active tab, its new index is the drop position
      newActiveTabIndex = index
    } else if (activeTabIndex !== null && newActiveTabIndex !== null) {
      // If dragging a tab across the active tab, adjust the active tab index
      if (draggedTab < activeTabIndex && index >= activeTabIndex) {
        // Dragging a tab from before to after the active tab - active tab shifts back
        newActiveTabIndex--
      } else if (draggedTab > activeTabIndex && index <= activeTabIndex) {
        // Dragging a tab from after to before the active tab - active tab shifts forward
        newActiveTabIndex++
      }
    }

    // First call the reorder callback
    onTabsReorder(newTabs)

    // Then update the active tab if it changed
    if (newActiveTabIndex !== activeTabIndex && newActiveTabIndex !== null) {
      onTabChange(newActiveTabIndex)
    }

    // Reset all dragging state
    resetDragState()
  }

  const handleDragEnd = (event: React.DragEvent) => {
    // Reset opacity
    if (event.currentTarget instanceof HTMLElement) {
      event.currentTarget.style.opacity = '1'
    }

    // Reset all drag states
    resetDragState()
  }

  const resetDragState = () => {
    setIsDragging(false)
    setDropIndicator(prev => ({ ...prev, show: false }))
    setDraggedTab(null)
    setDragOverTab(null)

    if (dragTimeout.current !== null) {
      window.clearTimeout(dragTimeout.current)
      dragTimeout.current = null
    }
  }

  // Hide drop indicator when mouse leaves the tab area
  useEffect(() => {
    const handleMouseLeave = () => {
      if (isDragging) {
        setDropIndicator(prev => ({ ...prev, show: false }))
      }
    }

    document.addEventListener('mouseleave', handleMouseLeave)

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave)

      // Cleanup any timeouts when component unmounts
      if (dragTimeout.current !== null) {
        window.clearTimeout(dragTimeout.current)
      }
    }
  }, [isDragging])

  const hasNoTabs = tabs.length === 0

  // Special handling for active tab when no tabs are present
  const effectiveActiveTabIndex = hasNoTabs ?
    -1 : (isDragging ? -1 : (activeTabIndex === null || activeTabIndex >= tabs.length ? 0 : activeTabIndex))

  return (
    <Box className={className} sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <StyledTabsContainer>
        <StyledTabs
          ref={tabsRef}
          value={effectiveActiveTabIndex}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          TabIndicatorProps={{ style: { display: isDragging ? 'none' : undefined } }}
        >
          {/* Drop indicator line - positioned absolutely relative to the tabs container */}
          {dropIndicator.show && draggedTab !== null && (
            <StyledDropIndicator
              style={{
                left: dropIndicator.left,
                height: dropIndicator.height,
              }}
            />
          )}
          {tabs.map((tab, index) => (
            <Tooltip
              key={tab.id}
              title={tab.label}
              placement="top"
              enterDelay={700}
              arrow
            >
              <StyledTab
                key={tab.id}
                disableRipple={disableTabRipple}
                draggable={!!onTabsReorder && tabs.length > 1}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnter={(e) => handleDragEnter(e, index)}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                onClick={(e) => { if (isDragging) e.preventDefault() }}
                className={`
                ${dragOverTab === index ? 'drag-over' : ''} 
                ${draggedTab === index ? 'dragging' : ''}
                ${!isDragging && activeTabIndex === index ? 'active-tab' : ''}
              `}
                label={
                  <StyledTabLabel
                    hasCloseButton={!!onTabClose}
                    className="tab-with-close-button"
                  >
                    {tab.color && (
                      <StyledTabColor $color={tab.color} />
                    )}
                    {tab.icon && (
                      <StyledTabIconContainer>
                        {tab.icon}
                      </StyledTabIconContainer>
                    )}
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{ maxWidth: maxLabelWidth }}
                    >
                      {tab.label}
                    </Typography>

                    {onTabClose && (
                      <StyledCloseButton
                        size="small"
                        disableRipple
                        onClick={(e) => handleTabClose(index, e)}
                      >
                        <Icon icon={faTimes} sx={{ fontSize: pxToRem(10) }} />
                      </StyledCloseButton>
                    )}
                  </StyledTabLabel>
                }
              />
            </Tooltip>
          ))}

          {showAddButton && (
            <StyledAddButton
              disableRipple={disableTabRipple}
              icon={<Icon sx={{ fontSize: pxToRem(14) }} icon={faPlus} />}
              value={tabs.length} // explicitly set value to length of tabs to match handleTabChange logic
            />
          )}
        </StyledTabs>
      </StyledTabsContainer>

      {/* Tab Panels */}
      <StyledTabPanelsContainer>
        {hasNoTabs ? (
          // Empty state when no tabs are present
          <StyledEmptyState>
            {emptyStateContent || (
              <Typography variant="body2" color="text.secondary">
                No tabs are open
              </Typography>
            )}
          </StyledEmptyState>
        ) : (
          // Normal tab panels
          tabs.map((tab, index) => (
            <StyledTabPanel
              key={tab.id}
              role="tabpanel"
              hidden={activeTabIndex !== index}
              id={`tabpanel-${tab.id}`}
              aria-labelledby={`tab-${tab.id}`}
              className={tabPanelClassName}
            >
              {activeTabIndex === index && tab.content}
            </StyledTabPanel>
          ))
        )}
      </StyledTabPanelsContainer>
    </Box>
  )
}


const StyledTabsContainer = styled(Box)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  position: 'relative',
  flexShrink: 0,
}))

const StyledTabPanelsContainer = styled(Box)({
  flexGrow: 1,
  position: 'relative',
  overflow: 'auto',
})

const StyledTabs = styled(Tabs)(() => ({
  minHeight: '32px',
  '& .MuiTabs-scrollButtons': {
    position: 'absolute',
    height: '100%',
    width: 28,
    zIndex: 2,
  },
  '& .MuiTabs-scrollButtons.Mui-disabled': {
    opacity: 0,
  },
  '& .MuiTabs-scrollButtons:first-of-type': {
    left: 0,
    background: 'linear-gradient(90deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.7) 100%)',
  },
  '& .MuiTabs-scrollButtons:last-of-type': {
    right: 0,
    background: 'linear-gradient(90deg, rgba(255,255,255,0.7) 0%, rgba(255,255,255,1) 100%)',
  },
  '& .MuiTabScrollButton-root': {
    width: '28px',
    height: '32px',
  },
  '& .MuiTabs-scroller': {
    margin: '0 !important',
  },
}))

const StyledTab = styled(Tab)(({ theme }) => ({
  minHeight: '32px',
  height: '32px',
  padding: '0 12px',
  fontSize: pxToRem(12),
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  position: 'relative',

  '&.active-tab': {
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      width: '100%',
      height: '2px',
      backgroundColor: theme.palette.primary.main,
    },
  },

  '&.Mui-selected': {
    '&::before': {
      opacity: 0,
    },
    '&.dragging': {
      color: 'inherit',
      '&::after': {
        display: 'none',
      },
    },
  },

  '&.drag-over': {
    backgroundColor: theme.palette.action.hover,
  },
  '&.dragging': {
    opacity: 0.6,
  },
}))

const StyledTabLabel = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'hasCloseButton',
})<{ hasCloseButton?: boolean }>(({ hasCloseButton }) => ({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  paddingRight: hasCloseButton ? 20 : 0,
  height: '100%',
}))

const StyledTabColor = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$color',
}) <{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
  margin-right: 6px;
`

const StyledTabIconContainer = styled(Box)({
  marginRight: 6,
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    fontSize: pxToRem(14),
  },
})

const StyledCloseButton = styled(IconButton)(() => ({
  position: 'absolute',
  right: -6,
  top: '50%',
  transform: 'translateY(-50%)',
  opacity: 0,
  transition: 'opacity 0.2s',
  padding: 2,
  width: 24,
  height: 24,
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.08)',
  },
  '.tab-with-close-button:hover &': {
    opacity: 1,
  },
}))

const StyledAddButton = styled(Tab)({
  minWidth: 0,
  minHeight: '32px',
  height: '32px',
  padding: '0 8px',
})

const StyledTabPanel = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(1),
  height: '100%',
}))

const StyledDropIndicator = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: 2,
  backgroundColor: theme.palette.primary.main,
  zIndex: 10,
  borderRadius: 1,
  boxShadow: '0 0 3px rgba(0, 0, 0, 0.2)',
  pointerEvents: 'none',
  willChange: 'transform',
  top: 0,
}))

// For empty state
const StyledEmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  color: theme.palette.text.secondary,
}))